<template>
  <div :class="['editor-card', { active, 'batch-mode': batchMode, 'is-deleted': deleted }]">
    <div class="editor-card__check" v-if="batchMode" @click="$emit('toggle-select')">
      <i :class="['fa', selected ? 'fa-check-square text-primary' : 'fa-square-o']"></i>
    </div>
    <div class="editor-card__body">
      <div class="editor-card__meta">
        <div class="meta-left">
          <span v-if="!active" class="meta-source-icon" :title="displaySource === 'x' ? 'X' : (displaySource === 'patch' ? 'iPastBook' : 'Facebook')">
            <!-- X 官方 logo：Twitter 独立书里所有 item 都挂这个 -->
            <svg v-if="displaySource === 'x'" viewBox="0 0 24 24" width="11" height="11">
              <path fill="#000" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            <svg v-else-if="displaySource === 'patch'" viewBox="0 0 48 48" width="11" height="11"><path fill="#999" d="M30.7 19.5c0-2.3-1.8-4.1-4.1-4.1H11.7C5.2 15.4 0 20.7 0 27.2v13.8C0 43.2 1.8 45 4.1 45s4.1-1.8 4.1-4.1V27.2c0-1.9 1.6-3.5 3.5-3.5h14.9c2.3 0 4.1-1.8 4.1-4.2z"/><path fill="#999" d="M28.8 0H4.2C1.8 0 0 1.8 0 4.1s1.8 4.1 4.1 4.1h24.5c6.1 0 11.1 5 11.1 11.1s-5 11.1-11.1 11.1h-10c-2.3 0-4.1 1.8-4.1 4.1s1.8 4.1 4.1 4.1h10C39.4 38.6 48 30 48 19.3S39.4 0 28.8 0z"/></svg>
            <i v-else class="fa fa-facebook"></i>
          </span>
          <span class="meta-date" v-if="!active">{{detailDate(item.postDate)}}</span>
          <div class="meta-date-edit" v-if="active">
            <input class="form-control" type="date" v-model="editDate">
            <input class="form-control" type="time" v-model="editTime">
          </div>
        </div>
        <div class="meta-actions" v-if="!batchMode && !active && !deleted">
          <span role="button" class="text-primary" @click="$emit('edit')">
            <i class="fa fa-pencil"></i> {{$t('content.buttonEdit')}}
          </span>
          <span role="button" class="text-danger" @click="$emit('delete')">
            <i class="fa fa-trash-o"></i> {{$t('content.buttonDelete')}}
          </span>
        </div>
        <div class="meta-actions" v-if="!batchMode && deleted">
          <span role="button" class="text-primary" @click="$emit('recover')">
            <i class="fa fa-undo"></i> {{$t('content.buttonRecover') || 'Recover'}}
          </span>
        </div>
      </div>
      <div class="editor-card__content" v-html="contentParser(item.content)" v-show="!active"></div>
      <textarea class="textarea editor-card__content" v-auto v-if="active" v-model="item.content"></textarea>
      <div class="editor-card__albums" v-if="images.length || active">
        <draggable :list="images" class="albums-wrap" draggable=".album-active" :disabled="!active">
          <div :class="['album-img', { 'album-active': active }]"
               v-for="(image, index) in images" :key="image[0]"
               @click="handleImageClick(image, index)">
            <div class="album-img__inner" :data-orient="image[4]"
                 v-lazy:background-image="getImageUrl(image[1], 320, false)"></div>
            <div class="album-img__action" v-show="active" @click.stop="handleImageClick(image, index)">
              <i class="fa fa-times"></i>
            </div>
          </div>
          <div class="album-img uploading" v-for="img in tempImages" :key="img.timestamp">
            <div class="album-img__inner" :data-orient="img.rotate"
                 :style="{backgroundImage: 'url(' + img.image + ')'}"></div>
            <block :done="img.dataLoaded"></block>
          </div>
          <div class="album-img uploader" v-show="active">
            <uploader @change="uploadImage" :mixed="true"/>
          </div>
        </draggable>
      </div>
      <!-- Footer: Cancel + Confirm (only in edit mode) -->
      <div class="editor-card__footer" v-if="active">
        <button class="btn plant" @click="$emit('cancel')">{{$t('editor.cancel')}}</button>
        <button class="btn primary" @click="saveItemChange">{{$t('editor.confirm')}}</button>
      </div>
    </div>
  </div>
</template>

<script>
import calculateNodeHeight from 'src/_utils/calculateNodeHeight'
import { findIndex } from 'lodash'
import api from 'src/api'
import { getImageUrl } from 'src/_utils/'
import EXIF from 'exif-js'
import Block from 'src/components/common/LoadingBlock.vue'
import draggable from 'vuedraggable'

export default {
  name: 'EditorCard',
  components: { Block, draggable },
  props: {
    item: { type: Object, required: true },
    active: { type: Boolean, default: false },
    batchMode: { type: Boolean, default: false },
    selected: { type: Boolean, default: false },
    deleted: { type: Boolean, default: false },
    // 强制改写 item._source 的显示。'x' = 在时间戳前显示官方 X 标志。
    // 不传则保持原来按 item._source 渲染（FB/iPastBook）。
    sourceOverride: { type: String, default: '' }
  },
  computed: {
    displaySource () {
      // 'x' | 'patch' | 'fb' (fallback — uses fa-facebook)
      if (this.sourceOverride) return this.sourceOverride
      return this.item._source === 'patch' ? 'patch' : 'fb'
    }
  },
  data () {
    const d = this.item.postDate ? new Date(this.item.postDate) : new Date()
    const pad = v => `0${v}`.substr(-2)
    return {
      images: [],
      tempImages: [],
      editDate: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      editTime: `${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
  },
  directives: {
    auto: {
      inserted (el) {
        const maxRows = window.innerWidth <= 767 ? 10 : null
        calculateNodeHeight(el, null, maxRows)
      },
      update (el) {
        const maxRows = window.innerWidth <= 767 ? 10 : null
        calculateNodeHeight(el, null, maxRows)
      }
    }
  },
  created () {
    const { current = [] } = this.item.pics || {}
    current.map(pic => pic.push(false))
    this.images = current
  },
  methods: {
    getImageUrl,
    contentParser (html) {
      return (html || '').replace(/\n/g, '<br>')
    },
    detailDate (date) {
      return this.$moment(date).format('YYYY/MM/DD HH:mm')
    },
    handleImageClick (image, index) {
      if (this.active) {
        this.images.splice(index, 1)
      } else {
        const imgs = this.images.filter(i => !i[2]).map(item => this.getImageUrl(item[1], 640, false))
        const realIndex = this.images.filter(i => !i[2]).indexOf(image)
        this.$viewer({ images: imgs, activeIndex: Math.max(0, realIndex) })
      }
    },
    async uploadImage (files) {
      if (this.tempImages.length) {
        this.$toast({ message: this.$t('editor.uploadInProgress') })
        return
      }
      const albums = []
      for (const image of files) {
        if (image.source) {
          albums.push(await this.readImageExif(image))
        } else {
          albums.push({ source: image, rotate: 0 })
        }
      }
      const images = []
      albums.map((file, index) => {
        const timestamp = window.btoa(Date.now() + index * 100)
        const form = new FormData()
        images.push({ ...file, timestamp })
        form.append('image', file.source)
        api.uploadImage(form).then(data => {
          if (!data || data.errors) {
            this.$toast({ message: this.$t('editor.uploadFailed'), position: 'bottom' })
            return
          }
          const imgIndex = findIndex(this.tempImages, { timestamp })
          let { origin, hostname } = window.location
          if (/localhost|m\.ipastbook/g.test(hostname)) origin = 'https://ipastbook.com'
          data.image = /http?s:/.test(data.image) ? data.image : `${origin}${data.image}`
          const img = [data.imageId, data.image, false, timestamp, file.rotate]
          if (file.image) img[1] = file.image
          this.tempImages.splice(imgIndex, 1)
          this.images.push(img)
        })
      })
      this.tempImages = this.tempImages.concat(images)
    },
    readImageExif (image) {
      return new Promise((resolve) => {
        try {
          EXIF.getData(image.source, function () {
            image.rotate = EXIF.getAllTags(this).Orientation || 0
            resolve(image)
          })
        } catch (e) {
          image.rotate = 0
          resolve(image)
        }
      })
    },
    saveItemChange () {
      if (this.tempImages.length) {
        this.$toast({ message: this.$t('editor.uploadInProgress') })
        return
      }
      const selectedPics = this.images.map(img => img[0])
      this.$emit('save', {
        content: this.item.content,
        title: this.item.title,
        selectedPics,
        postDate: `${this.editDate}T${this.editTime}`
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.editor-card {
  display: flex;
  border: 1px dashed transparent;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 0;
  transition: border-color 0.2s, opacity 0.2s;
  border-bottom: 1px solid #eee;

  &.is-deleted {
    opacity: 0.6;
    &:hover { opacity: 1; }
  }

  &:hover {
    border-color: #ccc;
    border-style: dashed;
  }

  &.active {
    border: 2px solid $el-color-primary;
    border-radius: 8px;
  }

  &__check {
    display: flex;
    align-items: flex-start;
    padding: 1rem 0 1rem 1rem;
    cursor: pointer;
    font-size: 20px;
    color: #ccc;

    .fa-check-square { color: $el-color-primary; }
  }

  &__body {
    flex: 1;
    padding: 1rem;
    min-width: 0;
  }

  &__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;

    .meta-left {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .meta-source-icon {
      width: 18px;
      height: 18px;
      border-radius: 4px;
      background: #eee;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      i { font-size: 10px; color: #999; }
    }

    .meta-date {
      color: #999;
      font-size: 13px;
      font-weight: 500;
    }

    .meta-date-edit {
      display: flex;
      gap: 8px;
      input[type="date"] {
        width: 150px;
      }
      input[type="time"] {
        width: 110px;
      }
      input {
        height: 32px;
        font-size: 13px;
        padding: 4px 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        &:focus { border-color: $el-color-primary; outline: none; }
      }
    }

    .meta-actions {
      display: flex;
      gap: 1rem;
      opacity: 0;
      transition: opacity 0.2s;

      .editor-card:hover & {
        opacity: 1;
      }

      [role=button] {
        cursor: pointer;
        font-size: 13px;
        i { margin-right: 4px; }
      }
      .text-danger { color: #999; &:hover { color: #ff7575; } }
    }
  }

  &__content {
    font-size: 14px;
    line-height: 1.6;
    word-wrap: break-word;
    margin-bottom: 0.5rem;

    &.textarea {
      width: 100%;
      min-height: 72px;
      border: 1px solid #ddd;
      border-radius: 6px;
      padding: 8px;
      font-size: 14px;
      resize: vertical;
    }
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    padding: 10px 0 0;
    margin-top: 10px;
    border-top: 1px solid #eee;
  }

  &__albums {
    .albums-wrap {
      display: flex;
      flex-wrap: wrap;
    }

    .album-img {
      width: 80px;
      height: 80px;
      border-radius: 6px;
      position: relative;
      overflow: hidden;
      margin-right: 8px;
      margin-bottom: 8px;
      background-color: #eee;
      cursor: zoom-in;

      &.album-active {
        cursor: pointer;
      }

      &__inner {
        position: absolute;
        top: 0; left: 0; width: 100%; height: 100%;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
      }

      &__action {
        position: absolute;
        top: 0; right: 0;
        width: 24px; height: 24px;
        background: rgba(0,0,0,0.5);
        border-radius: 0 0 0 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 12px;
      }

      &.deleted::before {
        content: '';
        position: absolute;
        top: 0; left: 0; width: 100%; height: 100%;
        background: rgba(0,0,0,0.5);
        z-index: 1;
      }
      &.deleted p {
        position: absolute;
        top: 50%; left: 0; width: 100%;
        text-align: center;
        color: #fff;
        font-size: 12px;
        margin-top: -8px;
        z-index: 2;
      }

      &.uploader {
        border: 1px dashed #ccc;
        cursor: pointer;
        background: #fafafa url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUAQMAAAC3R49OAAAABlBMVEUAAADd3d3swgqXAAAAAXRSTlMAQObYZgAAABZJREFUCFtjZPggwIgP/2f4iIIJqAcAuP0X1ZQ/+6oAAAAASUVORK5CYII=) center no-repeat;
        label {
          position: absolute;
          top: 0; left: 0; width: 100%; height: 100%;
          cursor: pointer;
        }
      }
    }
  }
}
</style>
